<template>
  <div v-if="hasActions"
       class="content-actions">
    <quick-add-button v-if="content.enableQuickAddForContent && isCartEnabled"
                      class="quick-add-btn"></quick-add-button>
    <div class="dropdown"
         data-unit="actions-dropdown"
         :class="{'is-active': toggleDropdown}">
      <div class="new-dropdown-trigger">
        <button class="button" aria-controls="dropdown-menu" @click="toggleDropdown = !toggleDropdown">
          <span class="icon is-small">
            <i v-if="toggleDropdown" class="fas fa-times"></i>
            <i v-else class="fas fa-angle-down"></i>
          </span>
        </button>
      </div>
      <div class="dropdown-menu" id="dropdown-menu" role="menu">
        <div class="dropdown-content">
          <div v-if="canEdit"
               class="dropdown-item">
            <router-link data-unit="edit-button"
                         class="dropdown-item"
                         target="_blank"
                         :to="editLink">
              <span class="icon mr-2 fas fa-edit" />
              <span>{{ $t('edit') }}</span>
            </router-link>
          </div>
          <div v-if="canFavorite"
               class="dropdown-item"
               data-unit="favorite-button"
               @click="addToFavorites">
            <span class="mr-2" :class="favoriteStarClass" />
            <span>{{ $t('favorites') }}</span>
          </div>
          <div v-if="canExport"
               class="dropdown-item"
               data-unit="export-button"
               @click="exportContent">
            <span class="icon mr-2 fas fa-file-export" />
            <span>{{ $t('export') }}</span>
          </div>
          <div v-if="canDownload"
               class="dropdown-item">
            <div v-if="hasEnabledIframeSupport && isBasicIFrame"
                 class="custom-download"
                 data-unit="download-button"
                 @click="customDownload(content.contentUrl)">
              <span class="icon mr-2 fas fa-download" />
              <span>{{ $t('download') }}</span>
            </div>
            <a v-else
               class="custom-download"
               data-unit="download-button"
               :href="content.contentUrl"
               :style="{ color: 'unset' }">
              <span class="icon mr-2 fas fa-download" />
              <span>{{ $t('download') }}</span>
            </a>
          </div>
          <a v-if="isKnowledgeBaseSupportedContentType && isKnowledgeBaseEnabled"
             href="#"
             class="dropdown-item"
             :class="{ 'is-disabled': isLoadingStatus || !isSidecarActionable }"
             @click.prevent="toggleKnowledgeBaseSidecar">
            <span style="display: flex; align-items: center;">
              <span class="icon mr-2">
                <img src="@/assets/icons/spark.png" alt="Knowledge Base">
              </span>
              <span>{{ $t('knowledgeBase.library.buttonText') }}</span>
            </span>
          </a>
          <div v-if="reportAnIssueEnabled"
               class="dropdown-item"
               @click="reportAnIssue">
            <span class="icon mr-2 fas fa-exclamation-triangle" />
            <span>{{ $t('reportAnIssue') }}</span>
          </div>
        </div>
      </div>
    </div>
    <div class="actions-buttons buttons pr-2">
      <router-link v-if="canEdit"
                   class="button"
                   :to="editLink"
                   target="_blank">
        <span class="icon fas fa-edit" />
        <span>{{ $t('edit') }}</span>
      </router-link>
      <button v-if="canFavorite"
              class="button"
              @click="addToFavorites">
        <span :class="favoriteStarClass" />
        <span>{{ $t('favorites') }}</span>
      </button>
      <button v-if="canExport"
              class="button"
              @click="exportContent">
        <span class="icon fas fa-file-export" />
        <span>{{ $t('export') }}</span>
      </button>
      <div v-if="canDownload"
           class="pr-2">
        <button v-if="hasEnabledIframeSupport && isBasicIFrame"
                class="button"
                @click="customDownload(content.contentUrl)">
          <span class="icon fas fa-download"></span>
          <span>{{ $t('download') }}</span>
        </button>
        <a v-else
           class="button"
           download
           :href="content.contentUrl">
          <span class="icon fas fa-download" />
          <span>{{ $t('download') }}</span>
        </a>
      </div>
      <bulma-tooltip v-if="isKnowledgeBaseSupportedContentType && isKnowledgeBaseEnabled"
                     position="is-bottom"
                     :disabled="!knowledgeBaseTooltip"
                     :label="knowledgeBaseTooltip">
        <button-label :label="$t('new')">
          <button class="button"
                  :disabled="isLoadingStatus || !isSidecarActionable"
                  @click="toggleKnowledgeBaseSidecar">
            <span class="icon">
              <img src="@/assets/icons/spark.png" alt="Knowledge Base">
            </span>
            <span>{{ $t('knowledgeBase.library.buttonText') }}</span>
          </button>
        </button-label>
      </bulma-tooltip>
      <button v-if="reportAnIssueEnabled"
              class="button"
              @click="reportAnIssue">
        <span class="icon fas fa-exclamation-triangle" />
        <span>{{ $t('reportAnIssue') }}</span>
      </button>
    </div>
    <add-to-favorites-modal v-if="addToFavoritesOpen"
                            :key="favoriteTarget.id"
                            :media="favoriteTarget"
                            @cancel="addToFavoritesOpen = false"
                            @favoritesUpdated="fetchFavorited()" />
    <export-modal v-if="exportModalOpen"
                  :disable-redirect="false"
                  :locale-hidden="false"
                  @exportContent="handleExport"
                  @cancel="exportModalOpen = false" />
    <report-an-issue-modal v-if="reportAnIssueModalOpened"
                           :report-context="activeReportContext"
                           @cancel="closeReportAnIssue" />
  </div>
</template>

<script>
import AddToFavoritesModal from './AddToFavoritesModal'
import BulmaTooltip from '@/components/common/BulmaTooltip.vue'
import ButtonLabel from '@/components/common/ButtonLabel.vue'
import ExportModal from './ExportModal'
import http from '@/http'
import QuickAddButton from '@/components/common/QuickAddButton.vue'
import ReportAnIssueModal from './ReportAnIssueModal'
import { customDownload } from '@/helpers/customDownload'
import { directive as onClickaway } from 'vue3-click-away'
import { mapActions, mapGetters } from 'vuex'

export default {
  name: 'ContentActions',
  props: {
    content: {
      type: Object,
      required: true
    },
    book: {
      type: Object,
      default: () => {}
    },
    hideExport: Boolean,
    reportAnIssueEnabled: {
      type: Boolean
    },
    reportContext: {
      type: Object,
      default: null
    },
    snapshotContext: {
      type: Object,
      default: null
    }
  },
  directives: {
    onClickaway
  },
  components: {
    AddToFavoritesModal,
    BulmaTooltip,
    ButtonLabel,
    ExportModal,
    QuickAddButton,
    ReportAnIssueModal
  },
  data () {
    return {
      addToFavoritesOpen: false,
      activeReportContext: null,
      exportModalOpen: false,
      favorited: false,
      isBasicIFrame: false,
      reportAnIssueModalOpened: false,
      toggleDropdown: false
    }
  },
  watch: {
    book: {
      immediate: true,
      handler () {
        this.fetchFavorited()
      }
    },
    content: {
      handler: 'fetchKnowledgeBaseStatus',
      immediate: true
    }
  },
  computed: {
    ...mapGetters({
      hasEnabledIframeSupport: 'user/hasEnabledIframeSupport',
      isCartEnabled: 'user/isCartOrErpCartEnabled'
    }),
    ...mapGetters('knowledgeBase', [
      'isSidecarOpen',
      'isLoadingStatus',
      'isKnowledgeBaseEnabled',
      'isSidecarActionable',
      'statusMessage'
    ]),
    hasDownloadOrExport () {
      // UG Setting: Book printing only applies to the book but not the chapters and pages
      const isBookPrintingDisabled = this.content.contentType === 'book' &&
        this.$store.getters['user/hasDisableBookPrinting']
      /*
       * Book-setting: Chapters and pages do not inherit disableExport from their parent.
       * Thus, TOC must be checked.
       */
      const isContentPrintingDisabled = (this.content?.disableExport ?? false) ||
        (this.content.toc?.book?.disableExport ?? false)
      // UG Setting
      const isPrintingDisabled = this.$store.getters['user/hasDisablePrinting']

      return !(isBookPrintingDisabled || isContentPrintingDisabled || isPrintingDisabled)
    },
    isPublisher () {
      return this.$store.getters['user/isPublisher']
    },
    hasActions () {
      return this.canEdit || this.canFavorite || this.canExport || this.canDownload
        || this.reportAnIssueEnabled
        || (this.isKnowledgeBaseSupportedContentType && this.isKnowledgeBaseEnabled)
    },
    canEdit () {
      return this.isPublisher && this.editLink !== null
    },
    canExport () {
      const contentType = this.content.contentType
      return !this.hideExport &&
        ['book', 'chapter', 'illustration'].includes(contentType) &&
        this.hasDownloadOrExport
    },
    canDownload () {
      const contentType = this.content.contentType
      return ['document', 'image', 'video'].includes(contentType) && this.hasDownloadOrExport
    },
    canFavorite () {
      const standardUser = !this.$store.state.user.generic
      return standardUser && !!this.favoriteTarget
    },
    editLinkName () {
      switch (this.content.type) {
        case 'part':
          return 'PartEditor'
        case 'page':
          return 'PageEditor'
        case 'media':
          return 'MediaEditor'
        case 'product-page':
          return 'ProductPageEditor'
        default:
          return null
      }
    },
    editLinkParams () {
      return {
        id: this.content.id
      }
    },
    editLink () {
      if (this.snapshotContext) {
        return {
          name: 'ThreeDModelSnapshots',
          params: {
            id: this.snapshotContext.threeDModelId
          },
          query: {
            snapshotId: this.snapshotContext.id
          }
        }
      }

      const name = this.editLinkName
      const params = this.editLinkParams

      if (name && params) {
        return { name, params }
      }

      return null
    },
    favoriteTarget () {
      if (this.book) {
        return this.book
      } else if (this.content.type === 'media') {
        return this.content
      } else if (this.content.type === 'product-page') {
        return this.content
      }
      return null
    },
    favoriteStarClass () {
      return [
        'icon',
        'fa-star',
        this.favorited ? 'fas' : 'far'
      ]
    },
    isKnowledgeBaseSupportedContentType () {
      return ['page', 'media'].includes(this.content.type)
        && this.content.contentType === 'document'
        && !this.$route.path.includes('details')
    },
    knowledgeBaseTooltip () {
      if (this.isKnowledgeBaseEnabled && !this.isSidecarActionable) {
        const statusType = this.$store.state.knowledgeBase.status.type
        switch (statusType) {
          case 'no_content':
            return this.$t('knowledgeBase.library.noContent')
          case 'service_error':
            return this.$t('knowledgeBase.library.serviceError')
          default:
            return this.statusMessage
        }
      }
      return null
    }
  },
  methods: {
    ...mapActions('knowledgeBase', {
      openSidecar: 'openSidecar',
      closeSidecar: 'closeSidecar',
      fetchStatus: 'fetchStatus'
    }),
    customDownload,
    addToFavorites () {
      this.addToFavoritesOpen = true
    },
    exportContent () {
      this.exportModalOpen = true
    },
    reportAnIssue () {
      this.activeReportContext = this.reportContext
        ? { ...this.reportContext }
        : null
      this.reportAnIssueModalOpened = true
    },
    closeReportAnIssue () {
      this.activeReportContext = null
      this.reportAnIssueModalOpened = false
    },
    toggleKnowledgeBaseSidecar() {
      if (this.isLoadingStatus || !this.isSidecarActionable) return

      if (this.isSidecarOpen) {
        this.closeSidecar()
      } else {
        this.openSidecar()
      }

      if (this.toggleDropdown) {
        this.toggleDropdown = false
      }
    },
    async fetchFavorited () {
      if (this.content.contentType !== "media") return

      // Identifiers are available differently between different content.
      const id = this.content?.id || (this.book && this.book.id)

      if (!id) return

      try {
        const { data } = await http.get(`medias/${id}/media-shelves`)
        this.favorited = data && data.length
      } catch {
        this.favorited = false
      }
    },
    async fetchKnowledgeBaseStatus() {
      if (this.isKnowledgeBaseSupportedContentType) {
        await this.fetchStatus({
          contentType: this.content.type,
          contentId: this.content.id
        })
      } else {
        await this.$store.dispatch('knowledgeBase/resetState')
      }
    },
    async handleExport (payload) {
      await this.$store.dispatch('content/exportContent', payload)
    }
  },
  mounted () {
    // Applicable to normal logins/TEK; widgets must be handled separately
    this.isBasicIFrame = window !== window.top && !this.$store.getters['widgets/isWidget']
  }
}
</script>

<style scoped lang="scss">
.custom-download {
  align-content: center;
  height: 56px;
  padding-left: 24px;
}
.content-actions {
  align-self: baseline;
  display: flex;
  flex-direction: row;

  .quick-add-btn {
    margin-right: .5em;
  }
}
.dropdown-menu {
  right: 0;
  left: unset;
}
.is-active {
  .button, .button:focus, .button.is-focused {
    border-color: hsl(217, 71%, 53%);
    color: #363636;
    box-shadow: 0 0 0 0.125em rgba(62, 122, 204, 0.25);
  }
}
.actions-buttons.buttons {
  justify-content: flex-end;
  flex-wrap: nowrap;
}
.button:focus, .button.is-focused, .button {
  box-shadow: unset;
  border-color: hsl(0, 0%, 71%);
  color: unset;
}
.dropdown-item a {
  margin: 0;
  padding: 0;
}
.dropdown-item:hover {
  background-color: hsl(0, 0%, 98%);
  color: #3E7ACC;

  a:hover {
    background-color: unset;
  }
}
@media only screen and (max-width: 768px) {
  .content-actions > .button > span {
    display: none;
  }
}

@media only screen and (min-width: 1025px) {
  .new-dropdown-trigger, .dropdown {
    display: none;
  }
}

@media only screen and (max-width: 1024px) {
  .actions-buttons {
    display: none;
  }
}
</style>
